import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import * as I from 'immutable';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {LoadingSpinner} from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import {LayoutWrapper} from 'app/components/LayoutWrapper';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization/types';
import {ApiProject} from 'app/modules/Remote/Project';
import {useWorkflowsContext} from 'app/pages/Agent/WorkflowsProvider';
import {LoggedInAuth, LoggedInUserActions} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import colors from 'app/utils/colorUtils';
import {isUpstreamStaff} from 'app/utils/featureFlags';
import {alphanumericSort} from 'app/utils/featureUtils';
import {formatDate} from 'app/utils/imageryUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {useArchiveAgentWorkflow} from './hooks';
import {NewWorkflowModal} from './NewWorkflowModal';
import cs from './styles.module.scss';

type WorkflowsPageProps = {
  auth: LoggedInAuth;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.ImmutableOf<ApiOrganization>;
  loggedInUserActions: LoggedInUserActions;
};

const WorkflowsEmptyState: React.FC = () => {
  // Pre-populated mailto link for customers without workflows
  const agentMailSubject = encodeURIComponent('Interested in Lens Agent');
  const agentMailBody = encodeURIComponent(
    "Hello Lens team, \n\nI'd like to learn more about Lens Agent and how to get set up for my organization."
  );
  const agentMailtoUrl = `mailto:lens@upstream.tech?subject=${agentMailSubject}&body=${agentMailBody}`;

  return (
    <div className={cs.emptyState}>
      <article className={cs.emptyStateArticle}>
        <div className={cs.emptyStateIcon} aria-hidden={true}>
          <B.Icon icon="clean" className={cs.agentText} />
        </div>
        <h2>Monitor at lightning speed</h2>
        <p>
          <strong>The Lens Agent helps you monitor efficiently and at scale</strong> — scanning
          imagery, flagging observations, and drafting notes automatically for review.{' '}
        </p>
        <p>
          Want to learn more? Tune into our webinar,{' '}
          <a
            href="https://www.upstream.tech/posts/coming-soon-your-automated-remote-monitoring-agent"
            target="_blank"
            rel="noopener noreferrer"
          >
            Meet the Lens agent
          </a>
          , read our{' '}
          <a
            href="https://lens.tech/resources/get-started-with-the-lens-agent/"
            target="_blank"
            rel="noopener noreferrer"
          >
            blog
          </a>
          , or reach out to <a href={agentMailtoUrl}>lens@upstream.tech</a>.
        </p>
      </article>
    </div>
  );
};

const WorkflowsPage: React.FC<WorkflowsPageProps> = ({
  auth,
  profile,
  organization,
  loggedInUserActions,
}) => {
  return (
    <LayoutWrapper
      auth={auth}
      profile={profile}
      organization={organization}
      loggedInUserActions={loggedInUserActions}
    >
      <Workflows
        organizationId={organization.get('id')}
        canUseCustomPrompt={isUpstreamStaff(profile)}
        canDelete={isUpstreamStaff(profile)}
      />
    </LayoutWrapper>
  );
};

type WorkflowsProps = {
  organizationId: string;
  canUseCustomPrompt: boolean;
  canDelete: boolean;
};

const deleteWorkflowMessage = (title: string) =>
  `Delete "${title}"? This removes the run plan and all of its runs, cancels anything still ` +
  `running, and dismisses observations that haven't been saved.`;

const Workflows: React.FC<WorkflowsProps> = ({organizationId, canUseCustomPrompt, canDelete}) => {
  const navigate = useNavigate();
  const {workflows, isFetchingWorkflows, refetch} = useWorkflowsContext();
  const archiveWorkflow = useArchiveAgentWorkflow(organizationId);
  const orgIdPrefix = routeUtils.makeUuidPrefix(organizationId);
  const [projects] = useProjects();
  const [isNewWorkflowModalOpen, setIsNewWorkflowModalOpen] = React.useState(false);

  const projectOptions = React.useMemo(() => {
    if (!projects) return [];
    // Sorted here rather than where they're rendered: the modal seeds its draft with the first
    // option, so the order has to be settled before anything reads it.
    return (projects.valueSeq().toArray() as I.ImmutableOf<ApiProject>[])
      .map((p) => ({
        name: p.get('name'),
        fcId: p
          .get('featureCollections')
          ?.find((fc) => fc?.get('kind') === 'primary')
          ?.get('id')
          .toString(),
      }))
      .sort((a, b) => alphanumericSort(a.name, b.name));
  }, [projects]);

  if (isFetchingWorkflows) {
    return <LoadingSpinner />;
  }

  if (!workflows || Object.keys(workflows).length === 0) {
    return (
      <div className={cs.page}>
        <div className={cs.pageContent}>
          <WorkflowsEmptyState />

          <div style={{marginTop: '1rem'}}>
            <B.Button
              intent={B.Intent.PRIMARY}
              className={cs.agentAction}
              text="New run plan"
              onClick={() => setIsNewWorkflowModalOpen(true)}
              disabled={!projectOptions || projectOptions.length === 0}
            />
          </div>
          {projectOptions && projectOptions.length > 0 && (
            <NewWorkflowModal
              isOpen={isNewWorkflowModalOpen}
              onClose={() => setIsNewWorkflowModalOpen(false)}
              projectOptions={projectOptions}
              organizationId={organizationId}
              canUseCustomPrompt={canUseCustomPrompt}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cs.page}>
      <div className={cs.pageContent}>
        <h2>Lens Agent</h2>
        <B.Callout
          icon={<B.Icon icon="clean" color={colors.purple} />}
          className={cs.activeLensAgent}
          style={{borderRadius: '0.5rem'}}
        >
          Your AI-powered partner for monitoring efficiently and at scale. Tell the Lens Agent what
          to monitor and it'll handle the rest from reviewing imagery, looking for changes, and
          drafting notes. You stay in the loop, it does the heavy lifting.
        </B.Callout>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3>Run plans</h3>
          <B.Button
            intent={B.Intent.PRIMARY}
            className={cs.agentAction}
            text="New run plan"
            onClick={() => setIsNewWorkflowModalOpen(true)}
            disabled={!projectOptions || projectOptions.length === 0}
          />
        </div>
        {projectOptions && projectOptions.length > 0 && (
          <NewWorkflowModal
            isOpen={isNewWorkflowModalOpen}
            onClose={() => {
              setIsNewWorkflowModalOpen(false);
              refetch();
            }}
            projectOptions={projectOptions}
            organizationId={organizationId}
            canUseCustomPrompt={canUseCustomPrompt}
          />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {workflows.map((workflow) => {
            const lastRun = workflow.lastRun;
            const latestRunId = lastRun?.id;
            return (
              <B.Card
                key={workflow.id}
                className={cs.planCard}
                interactive={true}
                onClick={() => {
                  navigate(`/${orgIdPrefix}/agent/workflows/${workflow.id}`);
                }}
              >
                <div>
                  <h3 style={{margin: '0'}}>{workflow.title}</h3>
                  <div className={cs.runTime}>
                    <div>{workflow.numFeatures} properties</div>
                    <div>•</div>
                    <div>{workflow.frequency ?? 'Unscheduled'}</div>
                    {lastRun && (
                      <>
                        <div>•</div>
                        <button
                          type="button"
                          className={cs.lastRunPill}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              routeUtils.agentWorkflowRunUrl(
                                organizationId,
                                workflow.id,
                                lastRun.id
                              )
                            );
                          }}
                        >
                          Last run {formatDate(lastRun.startedAt)}
                          <span aria-hidden={true}>·</span>
                          <span className={cs.findingsCount}>
                            {lastRun.findingsCount}{' '}
                            {lastRun.findingsCount === 1 ? 'finding' : 'findings'}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className={cs.planCardActions}>
                  {latestRunId && (
                    <B.Button
                      text="Review latest results"
                      minimal
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          routeUtils.agentWorkflowRunUrl(organizationId, workflow.id, latestRunId)
                        );
                      }}
                    />
                  )}
                  <B.Icon
                    icon="clean"
                    intent="primary"
                    className={classNames(cs.agentText, cs.planCardSparkle)}
                    size={24}
                  />
                  {canDelete && (
                    <B.Tooltip content="Delete run plan">
                      <B.Button
                        icon="trash"
                        minimal
                        className={cs.planCardDelete}
                        disabled={
                          archiveWorkflow.isPending && archiveWorkflow.variables === workflow.id
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(deleteWorkflowMessage(workflow.title))) {
                            archiveWorkflow.mutate(workflow.id);
                          }
                        }}
                      />
                    </B.Tooltip>
                  )}
                </div>
              </B.Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkflowsPage;
